import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';

import type { ContributionInfoResponseDto } from '@/api/campaign/grass/grass.dto';
import type { GrassCampaignResponseDto } from '@/api/campaign/grass/grass.dto';
import Footer from '@/components/common/Footer';
import { GNB } from '@/components/gnb';
import {
  useCertificateInfo,
  useGithubCallback,
  useGithubData,
  useTotalContribution,
} from '@/hooks/react-query/campaign';
import { useGrassDonationModal } from '@/store/campaign';
import { useLoginFailedModal } from '@/store/campaign/failed';

import { ToastProvider } from '../completion/components/Toast';
import { LoginFailedModal } from './components/Campaign/components/LoginFailedModal/template';
import { Campaign } from './components/Campaign/template';
import { EventInvitation } from './components/EventInvitation/template';
import { GrassCount } from './components/GrassCount/template';
import { GrassDonationModal } from './components/GrassDonationModal';
import { Hero } from './components/Hero/template';
import { Intro } from './components/Intro/template';
import { Join } from './components/Join';
import { KeepCommit } from './components/KeepCommit/template';

const DynamicCampaign = dynamic(() => Promise.resolve(Campaign), {
  ssr: false,
});

export function CampaignTemplate() {
  const router = useRouter();
  const { open, close } = useGrassDonationModal();
  const { open: loginFailedModalOpen } = useLoginFailedModal();
  const { mutate: handleGithubCallback } = useGithubCallback();
  const { data: grassData } = useGithubData();
  const { data: contributionData } = useTotalContribution();
  const { data: certificateData } = useCertificateInfo(
    router.query.id as string,
  );

  const modalData = router.query.id ? certificateData : grassData;
  const dynamicCampaignRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!router.query.id) {
      document.body.style.overflow = 'unset';
    }
  }, [router.query.id]);

  useEffect(() => {
    if (router.query.id) {
      open();
      document.body.style.overflow = 'hidden';
      return;
    }
  }, [router.query.id, open, loginFailedModalOpen, grassData, close]);

  useEffect(() => {
    const code = router.query.code;

    if (code && typeof code === 'string') {
      handleGithubCallback(code, {
        onSuccess: (data) => {
          if (data) {
            setTimeout(() => {
              dynamicCampaignRef.current?.scrollIntoView({
                behavior: 'smooth',
              });
            }, 100);
          }
        },
        onError: (error) => {
          console.error('GitHub OAuth Callback Error:', error);
        },
      });
    }
  }, [router.query.code, handleGithubCallback]);

  return (
    <ToastProvider>
      <GNB />
      <Hero />
      <GrassCount
        totalContributionData={contributionData?.totalContributions}
      />
      <Intro />
      <div ref={dynamicCampaignRef}>
        <DynamicCampaign
          grassData={grassData as GrassCampaignResponseDto}
          totalContributionData={contributionData?.totalContributions}
        />
      </div>
      <KeepCommit
        contributionData={contributionData as ContributionInfoResponseDto}
      />
      <Join />
      <EventInvitation />
      <Footer />
      <GrassDonationModal grassData={modalData as GrassCampaignResponseDto} />
      <LoginFailedModal />
    </ToastProvider>
  );
}
